import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactLoading from "react-loading";
import { motion } from "framer-motion";
import Breadcrumb from "../../../components/breadcrumb";
import Footer from "../../../components/footer";
import PageBackgroundPattern from "../../../components/PageBackgroundPattern";
import escudo from "../../../images/escudo.png";
import usePlayoffsData from "../hooks/usePlayoffsData";

const STAGE_ORDER = [
    'play-in',
    'cuartos de final',
    'semifinal',
    'final',
];



function RivalInitialBadge({ rival }) {
    return (
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
            <span className="text-sm font-black text-slate-400 uppercase select-none">
                {rival ? rival.charAt(0) : "?"}
            </span>
        </div>
    );
}

function SeriesListItem({ serie, isExpanded, onToggle, onPartidoNavigate }) {
    const esGanada = serie.resultado === "ganada";
    const esEnCurso = serie.resultado === "en-curso";

    return (
        <div className="border-b border-white/10 last:border-b-0">
            <button
                type="button"
                onClick={onToggle}
                disabled={serie.partidos.length === 0}
                className={`grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-3 text-left transition-colors sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:gap-5 sm:px-4 ${serie.partidos.length > 0 ? "hover:bg-white/5" : "cursor-default"}`}
                aria-expanded={serie.partidos.length > 0 ? isExpanded : undefined}
            >
                <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase text-slate-500">{serie.ronda}</p>
                    <h3 className="truncate text-sm font-bold text-white sm:text-base">{serie.rival}</h3>
                    <p className="text-[11px] text-slate-400 sm:text-xs">
                        {serie.aguadaPosicion} Aguada · {serie.rivalPosicion} {serie.rival}
                        {serie.alMejorDe ? ` · Al mejor de ${serie.alMejorDe}` : ""}
                    </p>
                </div>
                <span className={`hidden text-xs font-bold sm:block ${esEnCurso ? "text-slate-400" : esGanada ? "text-green-400" : "text-red-400"}`}>
                    {esEnCurso ? "EN CURSO" : esGanada ? "GANADA" : "PERDIDA"}
                </span>
                <span className="whitespace-nowrap text-lg font-black text-white sm:text-xl">
                    <span className={esGanada ? "text-green-400" : "text-slate-300"}>{serie.aguadaScore}</span>
                    <span className="px-1 text-slate-500">-</span>
                    <span className={!esEnCurso && !esGanada ? "text-red-400" : "text-slate-300"}>{serie.rivalScore}</span>
                </span>
                {serie.partidos.length > 0 && (
                    <span className={`text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} aria-hidden="true">›</span>
                )}
            </button>

            {isExpanded && serie.partidos.length > 0 && (
                <div className="bg-black/20 px-3 py-2 sm:px-4 sm:py-3">
                    {serie.partidos.map((partido) => (
                        partido.partidoId ? (
                            <Link
                                key={partido.numero}
                                to={`/partido/${partido.partidoId}`}
                                onClick={() => onPartidoNavigate?.()}
                                className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-md px-2 py-2 hover:bg-white/5"
                            >
                                <span className="text-[10px] font-black text-slate-500">J{partido.numero}</span>
                                <span className="truncate text-xs text-slate-400">{partido.local ? "Local" : "Visita"}{partido.cancha ? ` · ${partido.cancha}` : ""}</span>
                                <span className={`whitespace-nowrap text-sm font-black ${partido.ganado ? "text-green-400" : "text-red-400"}`}>{partido.scoreAguada} - {partido.scoreRival}</span>
                            </Link>
                        ) : (
                            <div key={partido.numero} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-2 py-2">
                                <span className="text-[10px] font-black text-slate-500">J{partido.numero}</span>
                                <span className="truncate text-xs text-slate-400">{partido.local ? "Local" : "Visita"}{partido.cancha ? ` · ${partido.cancha}` : ""}</span>
                                <span className={`whitespace-nowrap text-sm font-black ${partido.ganado ? "text-green-400" : "text-red-400"}`}>{partido.scoreAguada} - {partido.scoreRival}</span>
                            </div>
                        )
                    ))}
                    {serie.comentarios && <p className="border-t border-white/5 px-2 pt-3 text-xs text-slate-400">{serie.comentarios}</p>}
                </div>
            )}
        </div>
    );
}

function SeriesCard({ serie, isExpanded, onToggle, onPartidoNavigate }) {
    const navigate = useNavigate();
    const esGanada = serie.resultado === "ganada";
    const esEnCurso = serie.resultado === "en-curso";

    // If Aguada had a higher (worse) seed number, they go on the right
    const aguadaADerecha =
        serie.aguadaPosicionNum != null &&
        serie.rivalPosicionNum != null &&
        serie.aguadaPosicionNum > serie.rivalPosicionNum;

    const leftScore = aguadaADerecha ? serie.rivalScore : serie.aguadaScore;
    const rightScore = aguadaADerecha ? serie.aguadaScore : serie.rivalScore;
    // Which side "won" the series (for score color)
    const leftIsWinner = aguadaADerecha ? !esGanada : esGanada;

    const AguadaPanel = (
        <div className="w-24 sm:w-28 flex flex-col items-center gap-1 text-center shrink-0">
            <img
                src={escudo}
                alt="Aguada"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
            <span className="w-full text-[10px] sm:text-xs font-bold text-white uppercase truncate">Aguada</span>
            <span className="text-[10px] text-slate-500">{serie.aguadaPosicion}</span>
        </div>
    );

    const RivalPanel = (
        <div className="w-24 sm:w-28 flex flex-col items-center gap-1 text-center shrink-0">
            <RivalInitialBadge rival={serie.rival} />
            <span className="w-full text-[10px] sm:text-xs font-bold text-white uppercase truncate">
                {serie.rival}
            </span>
            <span className="text-[10px] text-slate-500">{serie.rivalPosicion}</span>
        </div>
    );

    return (
        <div
            onClick={serie.partidos.length > 0 ? onToggle : undefined}
            className={`bg-[#102216] border border-white/10 rounded-xl overflow-hidden transition-all duration-200 ${
                serie.partidos.length > 0 ? "cursor-pointer" : ""
            } ${
                esEnCurso
                    ? "border-t-[3px] border-t-slate-500 hover:border-slate-400/30"
                    : esGanada
                    ? "border-t-[3px] border-t-green-500 hover:border-green-500/30"
                    : "border-t-[3px] border-t-red-500 hover:border-red-500/30"
            }`}
        >
            {/* Card header */}
            <div className="p-4 sm:p-6 flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center sm:gap-6">
                {/* Round info */}
                <div>
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-slate-400">
                        {serie.temporada}
                    </span>
                    <h3 className="text-base sm:text-xl font-black text-white uppercase mt-0.5 leading-tight">
                        {serie.ronda}
                    </h3>
                    {serie.alMejorDe && (
                        <span className="text-[10px] text-slate-500 font-medium">
                            Al mejor de {serie.alMejorDe}
                        </span>
                    )}
                </div>

                {/* Scoreboard */}
                <div className="flex items-center justify-center gap-4 sm:gap-8">
                    {aguadaADerecha ? RivalPanel : AguadaPanel}

                    {/* Score */}
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex items-baseline gap-1.5 sm:gap-2 font-black text-4xl sm:text-5xl">
                            <span
                                className={
                                    esEnCurso
                                        ? "text-white"
                                        : leftIsWinner
                                        ? "text-white"
                                        : "text-slate-400"
                                }
                            >
                                {leftScore}
                            </span>
                            <span className="text-xl sm:text-2xl text-slate-500 font-bold">-</span>
                            <span
                                className={
                                    esEnCurso
                                        ? "text-white"
                                        : leftIsWinner
                                        ? "text-slate-400"
                                        : "text-white"
                                }
                            >
                                {rightScore}
                            </span>
                        </div>
                        {esEnCurso ? (
                            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-0.5 rounded-full bg-slate-500/15 text-slate-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
                                EN CURSO
                            </span>
                        ) : (
                            <span
                                className={`text-[10px] font-bold uppercase tracking-widest px-3 py-0.5 rounded-full ${
                                    esGanada
                                        ? "bg-green-500/15 text-green-400"
                                        : "bg-red-500/15 text-red-400"
                                }`}
                            >
                                {esGanada ? "GANADA" : "PERDIDA"}
                            </span>
                        )}
                    </div>

                    {aguadaADerecha ? AguadaPanel : RivalPanel}
                </div>

                {/* Chevron indicator */}
                <div className="flex justify-end">
                    {serie.partidos.length > 0 && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`w-5 h-5 transition-transform duration-200 text-slate-500 ${
                                isExpanded ? "rotate-90" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </div>
            </div>

            {/* Expanded: game schedule */}
            {isExpanded && serie.partidos.length > 0 && (
                <div onClick={(e) => e.stopPropagation()} className="border-t border-white/5 bg-black/20 px-4 sm:px-6 py-4 sm:py-5">
                    <h4 className="text-xs font-black text-slate-300 uppercase tracking-widest mb-3">
                        Partidos de la serie
                    </h4>
                    <div className="space-y-1.5">
                        {serie.partidos.map((p) => (
                            <div
                                key={p.numero}
                                role={p.partidoId ? "button" : undefined}
                                tabIndex={p.partidoId ? 0 : undefined}
                                onClick={p.partidoId ? () => {
                                    onPartidoNavigate?.();
                                    navigate(`/partido/${p.partidoId}`);
                                } : undefined}
                                onKeyDown={p.partidoId ? (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onPartidoNavigate?.();
                                        navigate(`/partido/${p.partidoId}`);
                                    }
                                } : undefined}
                                className={`flex items-center justify-between px-3 py-2.5 rounded-lg border border-transparent transition-all ${
                                    p.partidoId
                                        ? "bg-white/[0.03] hover:bg-white/5 hover:border-white/10 cursor-pointer"
                                        : "bg-white/[0.02]"
                                }`}
                            >
                                <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                                    <span className="text-[10px] font-black text-slate-500 uppercase w-7 shrink-0">
                                        J{p.numero}
                                    </span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase hidden sm:inline-block shrink-0 bg-slate-500/10 text-slate-400">
                                        {p.local ? "Local" : "Visita"}
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase hidden sm:inline-block shrink-0 ${p.ganado ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                                        {p.ganado ? "Ganado" : "Perdido"}
                                    </span>
                                    {p.estadoSerie && (
                                        <span className="text-[10px] text-slate-500 font-medium">
                                            {p.estadoSerie}
                                        </span>
                                    )}
                                    {p.cancha && (
                                        <span className="text-[10px] text-slate-500 font-medium truncate max-w-[160px] sm:max-w-[220px]">
                                            En {p.cancha}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <span
                                        className={`text-base sm:text-lg font-black ${
                                            p.ganado ? "text-green-400" : "text-red-400"
                                        }`}
                                    >
                                        {p.scoreAguada} - {p.scoreRival}
                                    </span>
                                    {p.partidoId && (
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                            Ir →
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {serie.comentarios && (
                        <p className="mt-4 text-xs text-slate-400 font-medium border-t border-white/5 pt-3">
                            {serie.comentarios}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

const PAGE_SIZE = 8;
const PLAYOFFS_VIEW_STATE_KEY = "playoffs:viewState:v1";

const normalizeTemporadaText = (value = "") => (
    String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[\u2010-\u2015]/g, "-")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
);

const resolveTemporadaFromQuery = (requestedTemporada, temporadas) => {
    if (!requestedTemporada) {
        return "Todas las temporadas";
    }

    const normalizedRequested = normalizeTemporadaText(requestedTemporada);
    const candidates = (temporadas || []).filter((t) => t !== "Todas las temporadas");

    const exactMatch = candidates.find(
        (temporada) => normalizeTemporadaText(temporada) === normalizedRequested
    );

    if (exactMatch) {
        return exactMatch;
    }

    const fuzzyMatch = candidates.find((temporada) => {
        const normalizedTemporada = normalizeTemporadaText(temporada);
        return (
            normalizedTemporada.includes(normalizedRequested)
            || normalizedRequested.includes(normalizedTemporada)
        );
    });

    return fuzzyMatch ?? "Todas las temporadas";
};

const readStoredPlayoffsViewState = () => {
    if (typeof window === "undefined") return null;

    try {
        const raw = window.sessionStorage.getItem(PLAYOFFS_VIEW_STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
    } catch (_error) {
        return null;
    }
};

const writeStoredPlayoffsViewState = (payload) => {
    if (typeof window === "undefined") return;

    try {
        window.sessionStorage.setItem(PLAYOFFS_VIEW_STATE_KEY, JSON.stringify(payload));
    } catch (_error) {
        // Ignore sessionStorage write errors (private mode/quota)
    }
};

function Playoffs() {
    const location = useLocation();
    const { isLoading, isError, series, temporadas } = usePlayoffsData();
    const [temporadaFiltro, setTemporadaFiltro] = useState("Todas las temporadas");
    const [rondaFiltro, setRondaFiltro] = useState(null);
    const [ligaFiltro, setLigaFiltro] = useState(() => {
        const liga = new URLSearchParams(location.search).get("liga");
        return ["primera", "lub", "federal"].includes(liga) ? liga : null;
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedId, setExpandedId] = useState(null);
    const [view, setView] = useState("cards");
    const [collapsedLeagues, setCollapsedLeagues] = useState(new Set());
    const [pendingScrollSeriesId, setPendingScrollSeriesId] = useState(null);
    const hasRestoredRef = useRef(false);

    useEffect(() => {
        document.title = "Playoffs";
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const liga = params.get("liga");
        const temporada = params.get("temporada");
        const ligaValida = ["primera", "lub", "federal"].includes(liga) ? liga : null;
        const hasQueryFilters = Boolean(liga || temporada);

        if (hasQueryFilters) {
            setLigaFiltro(ligaValida);
            setTemporadaFiltro(resolveTemporadaFromQuery(temporada, temporadas));
            setRondaFiltro(null);
            setCurrentPage(1);
            setExpandedId(null);
            setCollapsedLeagues(new Set());
            hasRestoredRef.current = true;
            return;
        }

        const storedState = readStoredPlayoffsViewState();
        if (storedState) {
            const restoredExpandedId =
                typeof storedState.expandedId === "string" ? storedState.expandedId : null;

            setLigaFiltro(
                ["primera", "lub", "federal"].includes(storedState.ligaFiltro)
                    ? storedState.ligaFiltro
                    : null
            );
            setTemporadaFiltro(
                typeof storedState.temporadaFiltro === "string"
                    ? storedState.temporadaFiltro
                    : "Todas las temporadas"
            );
            setRondaFiltro(
                typeof storedState.rondaFiltro === "string" ? storedState.rondaFiltro : null
            );
            setCurrentPage(
                Number.isInteger(storedState.currentPage) && storedState.currentPage > 0
                    ? storedState.currentPage
                    : 1
            );
            setExpandedId(restoredExpandedId);
            setCollapsedLeagues(
                Array.isArray(storedState.collapsedLeagues)
                    ? new Set(storedState.collapsedLeagues.filter((item) => typeof item === "string"))
                    : new Set()
            );
            setView(storedState.view === "list" || storedState.view === "cards" ? storedState.view : "cards");
            setPendingScrollSeriesId(
                typeof storedState.scrollTargetSeriesId === "string"
                    ? storedState.scrollTargetSeriesId
                    : restoredExpandedId
            );
            hasRestoredRef.current = true;
            return;
        }

        setLigaFiltro(ligaValida);
        setTemporadaFiltro(resolveTemporadaFromQuery(temporada, temporadas));
        setRondaFiltro(null);
        setCurrentPage(1);
        setExpandedId(null);
        setCollapsedLeagues(new Set());
        hasRestoredRef.current = true;
    }, [location.search, temporadas]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!hasRestoredRef.current) return;

        const payload = {
            temporadaFiltro,
            rondaFiltro,
            ligaFiltro,
            currentPage,
            expandedId,
            collapsedLeagues: [...collapsedLeagues],
            view,
        };

        writeStoredPlayoffsViewState(payload);
    }, [temporadaFiltro, rondaFiltro, ligaFiltro, currentPage, expandedId, collapsedLeagues, view]);

    const seriesPorTemporada = series.filter((s) => {
        if (temporadaFiltro !== "Todas las temporadas" && s.temporada !== temporadaFiltro) return false;
        if (ligaFiltro === 'primera' && !/LUB|Federal/i.test(s.temporada)) return false;
        if (ligaFiltro === 'lub' && !/LUB/i.test(s.temporada)) return false;
        if (ligaFiltro === 'federal' && !/Federal/i.test(s.temporada)) return false;
        return true;
    });

    const rondas = useMemo(() => {
        const unique = [...new Set(seriesPorTemporada.map((s) => s.ronda).filter(Boolean))];
        return unique.sort((a, b) => {
            const ai = STAGE_ORDER.indexOf(a.toLowerCase());
            const bi = STAGE_ORDER.indexOf(b.toLowerCase());
            if (ai === -1 && bi === -1) return a.localeCompare(b);
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
    }, [seriesPorTemporada]);

    const seriesFiltradas = seriesPorTemporada.filter(
        (s) => rondaFiltro === null || s.ronda === rondaFiltro
    );

    const totalPages = Math.ceil(seriesFiltradas.length / PAGE_SIZE);
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const seriesPaginadas = seriesFiltradas.slice(pageStart, pageStart + PAGE_SIZE);

    useEffect(() => {
        if (totalPages === 0 && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }

        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (isLoading || isError || !pendingScrollSeriesId) return;

        const seriesElement = document.getElementById(`serie-${pendingScrollSeriesId}`);
        if (!seriesElement) return;

        const navbarOffset = 90;
        const top = seriesElement.getBoundingClientRect().top + window.scrollY - navbarOffset;

        window.scrollTo({
            top: Math.max(top, 0),
            behavior: "smooth",
        });

        setPendingScrollSeriesId(null);
    }, [pendingScrollSeriesId, isLoading, isError, currentPage, seriesPaginadas]);

    function handlePartidoNavigation(serieId) {
        writeStoredPlayoffsViewState({
            temporadaFiltro,
            rondaFiltro,
            ligaFiltro,
            currentPage,
            expandedId: serieId,
            collapsedLeagues: [...collapsedLeagues],
            scrollTargetSeriesId: serieId,
            view,
        });
    }

    function handleFilterChange(value) {
        setTemporadaFiltro(value);
        setLigaFiltro(null);
        setRondaFiltro(null);
        setCurrentPage(1);
        setExpandedId(null);
        setCollapsedLeagues(new Set());
    }

    function handleLigaFiltroChange(value) {
        setLigaFiltro((prev) => (prev === value ? null : value));
        setTemporadaFiltro("Todas las temporadas");
        setCurrentPage(1);
        setExpandedId(null);
        setCollapsedLeagues(new Set());
    }

    function handleToggleLeague(liga) {
        setCollapsedLeagues((prev) => {
            const next = new Set(prev);
            if (next.has(liga)) next.delete(liga);
            else next.add(liga);
            return next;
        });
    }

    function handleRondaChange(ronda) {
        setRondaFiltro((prev) => (prev === ronda ? null : ronda));
        setCurrentPage(1);
        setExpandedId(null);
        setCollapsedLeagues(new Set());
    }

    function handlePageChange(page) {
        setCurrentPage(page);
        setExpandedId(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function handleToggle(id) {
        setExpandedId((prev) => (prev === id ? null : id));
    }

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#062a1a] pt-16">
            <PageBackgroundPattern />
            <main className="relative z-10 max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-10 pt-2 pb-8 sm:pb-10">
                <Breadcrumb
                    items={[
                        { label: "Inicio", to: "/" },
                        { label: "Playoffs", isCurrent: true },
                    ]}
                    className="mb-3 sm:mb-4"
                />

                {/* Page header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 sm:mb-10">
                    <div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2 tracking-tight border-l-4 border-red-500 pl-3 sm:pl-4 leading-tight">
                            Series de playoffs
                        </h1>
                        <p className="text-slate-400 font-medium text-base sm:text-lg max-w-2xl">
                            Registro de cada serie playoffs disputada en postemporada desde 1999.
                        </p>
                    </div>

                    {/* Season filter — only shown once data is ready */}
                    {!isLoading && !isError && temporadas.length > 1 && (
                        <div className="space-y-2 shrink-0">
                            <label className="text-sm font-semibold text-slate-200 px-1 block">
                                Temporada
                            </label>
                            <div className="relative w-full sm:w-64">
                                <select
                                    value={temporadaFiltro}
                                    onChange={(e) => handleFilterChange(e.target.value)}
                                    className="w-full bg-white/5 backdrop-blur-xl border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none cursor-pointer"
                                >
                                    {temporadas.map((t) => (
                                        <option key={t} value={t} className="bg-[#0c3a24]">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true"><path d="m5 7 5 5 5-5" /></svg>
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 pt-1">
                                {[
                                    { value: 'primera', label: '1ra División' },
                                    { value: 'lub', label: 'LUB' },
                                    { value: 'federal', label: 'Federal' },
                                ].map(({ value, label }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => handleLigaFiltroChange(value)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border backdrop-blur-xl transition-all select-none ${
                                            ligaFiltro === value
                                                ? "bg-green-500/15 text-green-400 border-green-500/40"
                                                : "bg-white/5 text-slate-400 border-white/10 hover:border-white/25 hover:text-slate-200"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Loading */}
                {isLoading && (
                    <div className="rounded-xl border border-white/10 bg-[#102216] p-6 sm:p-10">
                        <ReactLoading
                            type="bars"
                            color="#22c55e"
                            width={60}
                            className="text-center m-auto mb-6 sm:mb-10 sm:[&>div]:!w-[80px]"
                        />
                        <p className="text-center text-slate-200 text-base sm:text-lg font-semibold">
                            Cargando series de playoffs...
                        </p>
                    </div>
                )}

                {/* Error */}
                {isError && !isLoading && (
                    <div className="rounded-xl border border-red-400/30 bg-red-950/20 p-6 sm:p-8 text-center text-red-200 text-sm sm:text-base font-medium">
                        No se pudieron cargar las series de playoffs en este momento.
                    </div>
                )}

                {/* Stage filter badges + results summary */}
                {!isLoading && !isError && rondas.length > 1 && (
                    <div className="flex flex-wrap items-center gap-2 mb-5">
                        {rondas.map((ronda) => (
                            <button
                                key={ronda}
                                type="button"
                                onClick={() => handleRondaChange(ronda)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border backdrop-blur-xl transition-all ${
                                    rondaFiltro === ronda
                                        ? "bg-green-500 text-[#062a1a] border-green-500"
                                        : "bg-white/5 text-slate-300 border-white/10 hover:border-green-500 hover:text-white"
                                }`}
                            >
                                {ronda}
                            </button>
                        ))}
                        {(temporadaFiltro !== "Todas las temporadas" || rondaFiltro !== null || ligaFiltro !== null) && (
                            <>
                                <div className="h-4 w-[1px] bg-white/20 mx-1" />
                                <p className="text-slate-400 text-xs sm:text-sm font-medium whitespace-nowrap">
                                    <span className="text-white font-bold">{seriesFiltradas.length}</span> series
                                </p>
                                <div className="h-4 w-[1px] bg-white/20" />
                                <div className="flex items-center gap-1.5 text-xs sm:text-sm font-medium">
                                    <span className="text-green-400 font-bold">{seriesFiltradas.filter((s) => s.resultado === 'ganada').length}G</span>
                                    <span className="text-slate-500">-</span>
                                    <span className="text-red-400 font-bold">{seriesFiltradas.filter((s) => s.resultado === 'perdida').length}P</span>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {!isLoading && !isError && (
                    <div className="mb-5 flex justify-end">
                        <div className="flex rounded-lg border border-white/15 bg-white/5 backdrop-blur-xl p-0.5 text-xs font-bold">
                            <button
                                type="button"
                                onClick={() => setView("list")}
                                className={`relative z-10 rounded-md px-2.5 py-1.5 transition-colors duration-200 ${view === "list" ? "text-[#062a1a]" : "text-slate-400 hover:text-white"}`}
                                aria-pressed={view === "list"}
                            >
                                {view === "list" ? (
                                    <motion.span
                                        layoutId="playoffs-view-toggle-pill"
                                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                        className="absolute inset-0 -z-10 rounded-md bg-green-500"
                                    />
                                ) : null}
                                Listado
                            </button>
                            <button
                                type="button"
                                onClick={() => setView("cards")}
                                className={`relative z-10 rounded-md px-2.5 py-1.5 transition-colors duration-200 ${view === "cards" ? "text-[#062a1a]" : "text-slate-400 hover:text-white"}`}
                                aria-pressed={view === "cards"}
                            >
                                {view === "cards" ? (
                                    <motion.span
                                        layoutId="playoffs-view-toggle-pill"
                                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                        className="absolute inset-0 -z-10 rounded-md bg-green-500"
                                    />
                                ) : null}
                                Tarjetas
                            </button>
                        </div>
                    </div>
                )}

                {/* Series list + stats grid */}
                {!isLoading && !isError && (
                    <>
                        {seriesFiltradas.length > 0 ? (
                            <>
                                <div className={view === "cards" ? "space-y-3 sm:space-y-4" : "overflow-hidden rounded-xl border border-white/20 bg-white/5 backdrop-blur-xl"}>
                                    {seriesPaginadas.map((serie, index) => {
                                        const globalIndex = pageStart + index;
                                        const isNewLeague =
                                            globalIndex === 0 ||
                                            seriesFiltradas[globalIndex - 1].temporada !== serie.temporada;
                                        const isLeagueCollapsed = collapsedLeagues.has(serie.temporada);
                                        return (
                                            <div key={serie._id} id={`serie-${serie._id}`}>
                                                {isNewLeague && (
                                                    <div className={`flex items-center gap-3 ${view === "list" ? "p-3 sm:p-4" : ""} ${globalIndex > 0 ? "mt-8 mb-1" : "mb-1"}`}>
                                                        <h2 className="text-xs sm:text-sm font-black text-slate-400 uppercase tracking-[0.18em]">
                                                            {serie.temporada}
                                                        </h2>
                                                        <div className="flex-1 h-px bg-white/10" />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleLeague(serie.temporada)}
                                                            className="flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-slate-200 uppercase tracking-widest transition-colors shrink-0"
                                                        >
                                                            <span>{isLeagueCollapsed ? "Mostrar" : "Ocultar"}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className={`w-3 h-3 transition-transform duration-200 ${isLeagueCollapsed ? "-rotate-90" : "rotate-90"}`}
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                                strokeWidth={2.5}
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                                {!isLeagueCollapsed && (view === "cards" ? (
                                                    <SeriesCard
                                                        serie={serie}
                                                        isExpanded={expandedId === serie._id}
                                                        onToggle={() => handleToggle(serie._id)}
                                                        onPartidoNavigate={() => handlePartidoNavigation(serie._id)}
                                                    />
                                                ) : (
                                                    <SeriesListItem
                                                        serie={serie}
                                                        isExpanded={expandedId === serie._id}
                                                        onToggle={() => handleToggle(serie._id)}
                                                        onPartidoNavigate={() => handlePartidoNavigation(serie._id)}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-white/10 space-y-2 sm:space-y-3">
                                        <p className="text-center text-xs sm:text-sm text-slate-400 font-medium">
                                            Página {currentPage} de {totalPages} · {seriesFiltradas.length} series
                                        </p>
                                        <div className="flex items-center justify-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                                disabled={currentPage <= 1}
                                                className="h-9 px-3 rounded-lg bg-[#102216] border border-white/10 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                                            >
                                                ←
                                            </button>
                                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                .filter((page) => {
                                                    if (totalPages <= 5) return true;
                                                    return Math.abs(page - currentPage) <= 2;
                                                })
                                                .map((page) => (
                                                    <button
                                                        key={page}
                                                        type="button"
                                                        onClick={() => handlePageChange(page)}
                                                        className={`h-9 min-w-9 px-3 rounded-lg text-sm font-bold border transition-colors ${
                                                            page === currentPage
                                                                ? "bg-green-500 text-[#062a1a] border-green-500"
                                                                : "bg-[#102216] text-slate-200 border-white/10 hover:border-green-500"
                                                        }`}
                                                    >
                                                        {page}
                                                    </button>
                                                ))}
                                            <button
                                                type="button"
                                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                                disabled={currentPage >= totalPages}
                                                className="h-9 px-3 rounded-lg bg-[#102216] border border-white/10 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-green-500 transition-colors"
                                            >
                                                →
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="rounded-xl border border-white/10 bg-[#102216] p-10 text-center">
                                <p className="text-slate-400 font-medium">
                                    No hay series registradas para esta temporada.
                                </p>
                            </div>
                        )}

                    </>
                )}
            </main>

            <div className="relative z-10">
                <Footer />
            </div>
        </div>
    );
}

export default Playoffs;
